import React, { useState } from 'react'
import { Card, ProgressBar, Badge, Accordion, ListGroup } from 'react-bootstrap'
import { Icon } from 'monday-ui-react-core'
import { Check, CloseSmall, Time } from 'monday-ui-react-core/icons'
import './MigrationTracker.scss'

export const MigrationTracker = ({ statusData, selectedMigrationId, onSelectMigration }) => {
  const [expandedProjects, setExpandedProjects] = useState(new Set())

  const toggleProject = (projectId) => {
    const newExpanded = new Set(expandedProjects)
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId)
    } else {
      newExpanded.add(projectId)
    }
    setExpandedProjects(newExpanded)
  }

  const getStatusIcon = (isActive, isComplete) => {
    if (isComplete) {
      return <Icon className="text-success" iconType={Icon.type.SVG} icon={Check} iconSize={20} />
    }
    if (isActive) {
      return <Icon className="text-primary" iconType={Icon.type.SVG} icon={Time} iconSize={20} />
    }
    return null
  }

  const getStatusBadge = (status) => {
    const statusMap = {
      in_progress: { variant: 'primary', text: 'In Progress' },
      done: { variant: 'success', text: 'Completed' },
      cancel: { variant: 'danger', text: 'Cancelled' },
    }
    const config = statusMap[status] || { variant: 'secondary', text: 'Unknown' }
    return <Badge bg={config.variant}>{config.text}</Badge>
  }

  const formatTime = (isoString) => {
    if (!isoString) return 'N/A'
    const date = new Date(isoString)
    return date.toLocaleTimeString()
  }

  const calculateDuration = (startTime, endTime) => {
    if (!startTime) return 'N/A'
    const start = new Date(startTime)
    const end = endTime ? new Date(endTime) : new Date()
    const durationMs = end - start
    const minutes = Math.floor(durationMs / 60000)
    const seconds = Math.floor((durationMs % 60000) / 1000)
    return `${minutes}m ${seconds}s`
  }

  if (!statusData) {
    return (
      <Card className="migration-tracker">
        <Card.Body>
          <p className="text-muted">No migration data available</p>
        </Card.Body>
      </Card>
    )
  }

  const activeMigrations = statusData.activeMigrations || []
  const allMigrations = statusData.activeMigrations || []

  return (
    <div className="migration-tracker">
      {/* Overall Progress */}
      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">Overall Progress</h5>
            {getStatusBadge(statusData.status)}
          </div>
          <ProgressBar
            now={statusData.overallPercentage || 0}
            label={`${statusData.overallPercentage || 0}%`}
            variant={statusData.status === 'done' ? 'success' : 'primary'}
            className="mb-2"
          />
          <div className="d-flex justify-content-between text-muted small">
            <span>
              {statusData.completedProjects || 0} / {statusData.totalProjects || 0} projects
              completed
            </span>
            <span>Started: {formatTime(statusData.startTime)}</span>
          </div>
        </Card.Body>
      </Card>

      {/* Active Migrations */}
      {activeMigrations.length > 0 && (
        <Card className="mb-3">
          <Card.Header>
            <h6 className="mb-0">
              Active Migrations ({activeMigrations.length})
            </h6>
          </Card.Header>
          <ListGroup variant="flush">
            {activeMigrations.map((migration) => (
              <ListGroup.Item
                key={migration.projectId}
                className={`migration-item ${
                  selectedMigrationId === migration.projectId ? 'selected' : ''
                }`}
                onClick={() => onSelectMigration && onSelectMigration(migration.projectId)}
                style={{ cursor: 'pointer' }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="d-flex align-items-center">
                    {getStatusIcon(migration.isActive, false)}
                    <strong className="ms-2">{migration.projectName}</strong>
                  </div>
                  <Badge bg="info">{migration.percentageComplete}%</Badge>
                </div>
                <ProgressBar
                  now={migration.percentageComplete}
                  variant="info"
                  className="mb-2"
                  style={{ height: '8px' }}
                />
                <div className="small text-muted">
                  <div className="d-flex justify-content-between">
                    <span>
                      Tasks: {migration.completedTasks}/{migration.totalTasks}
                    </span>
                    <span>
                      Issues: {migration.completedIssues}/{migration.totalIssues}
                    </span>
                    <span>
                      Docs: {migration.completedDocuments}/{migration.totalDocuments}
                    </span>
                  </div>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Card>
      )}

      {/* All Migrations */}
      <Card>
        <Card.Header>
          <h6 className="mb-0">All Migrations ({allMigrations.length})</h6>
        </Card.Header>
        <Accordion flush>
          {allMigrations.map((migration, index) => (
            <Accordion.Item eventKey={index.toString()} key={migration.projectId}>
              <Accordion.Header>
                <div className="d-flex justify-content-between align-items-center w-100 me-3">
                  <div className="d-flex align-items-center">
                    {getStatusIcon(migration.isActive, !migration.isActive)}
                    <span className="ms-2">{migration.projectName}</span>
                  </div>
                  <Badge bg={migration.isActive ? 'primary' : 'success'}>
                    {migration.percentageComplete}%
                  </Badge>
                </div>
              </Accordion.Header>
              <Accordion.Body>
                <div className="migration-details">
                  <div className="mb-3">
                    <strong>Progress:</strong>
                    <ProgressBar
                      now={migration.percentageComplete}
                      label={`${migration.percentageComplete}%`}
                      variant={migration.isActive ? 'primary' : 'success'}
                      className="mt-2"
                    />
                  </div>

                  <div className="row mb-3">
                    <div className="col-3">
                      <div className="stat-card">
                        <div className="stat-label">Tasks</div>
                        <div className="stat-value">
                          {migration.completedTasks}/{migration.totalTasks}
                        </div>
                        {migration.failedTasks.length > 0 && (
                          <div className="stat-failed text-danger">
                            {migration.failedTasks.length} failed
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-3">
                      <div className="stat-card">
                        <div className="stat-label">Issues</div>
                        <div className="stat-value">
                          {migration.completedIssues}/{migration.totalIssues}
                        </div>
                        {migration.failedIssues.length > 0 && (
                          <div className="stat-failed text-danger">
                            {migration.failedIssues.length} failed
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-3">
                      <div className="stat-card">
                        <div className="stat-label">Documents</div>
                        <div className="stat-value">
                          {migration.completedDocuments}/{migration.totalDocuments}
                        </div>
                        {migration.failedDocuments.length > 0 && (
                          <div className="stat-failed text-danger">
                            {migration.failedDocuments.length} failed
                          </div>
                        )}
                      </div>
                    </div>
                    {migration.totalUpdates > 0 && (
                      <div className="col-3">
                        <div className="stat-card">
                          <div className="stat-label">Updates</div>
                          <div className="stat-value">
                            {migration.completedUpdates}/{migration.totalUpdates}
                          </div>
                          {migration.failedUpdates && migration.failedUpdates.length > 0 && (
                            <div className="stat-failed text-danger">
                              {migration.failedUpdates.length} failed
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mb-2">
                    <strong>Timeline:</strong>
                    <div className="small text-muted mt-1">
                      <div>Started: {formatTime(migration.startTime)}</div>
                      {migration.endTime && <div>Ended: {formatTime(migration.endTime)}</div>}
                      <div>
                        Duration: {calculateDuration(migration.startTime, migration.endTime)}
                      </div>
                    </div>
                  </div>

                  {/* Failed Items */}
                  {(migration.failedTasks.length > 0 ||
                    migration.failedIssues.length > 0 ||
                    migration.failedDocuments.length > 0 ||
                    (migration.failedUpdates && migration.failedUpdates.length > 0)) && (
                    <div className="mt-3">
                      <strong className="text-danger">Failed Items:</strong>
                      {migration.failedTasks.length > 0 && (
                        <div className="mt-2">
                          <div className="small fw-bold">Tasks:</div>
                          <ul className="small text-danger mb-2">
                            {migration.failedTasks.map((error, idx) => (
                              <li key={idx}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {migration.failedIssues.length > 0 && (
                        <div className="mt-2">
                          <div className="small fw-bold">Issues:</div>
                          <ul className="small text-danger mb-2">
                            {migration.failedIssues.map((error, idx) => (
                              <li key={idx}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {migration.failedDocuments.length > 0 && (
                        <div className="mt-2">
                          <div className="small fw-bold">Documents:</div>
                          <ul className="small text-danger mb-2">
                            {migration.failedDocuments.map((error, idx) => (
                              <li key={idx}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {migration.failedUpdates && migration.failedUpdates.length > 0 && (
                        <div className="mt-2">
                          <div className="small fw-bold">Updates:</div>
                          <ul className="small text-danger mb-2">
                            {migration.failedUpdates.map((error, idx) => (
                              <li key={idx}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      </Card>
    </div>
  )
}
